import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    ArrowLeft, ArrowRight, Check, ChevronDown, Leaf, Scale, Heart,
    ChefHat, Sparkles, Loader2, MessageCircle,
    Users, Dumbbell, Briefcase, CalendarRange, Baby, GraduationCap,
    Clock, HeartPulse, Sprout, Snowflake, Repeat, Truck, Salad as SaladIcon, Quote, Star,
} from "lucide-react";
import { BRAND } from "@/lib/brand";
import { API_URL } from "@/lib/api";

const API = API_URL;

// MarFit palette (from mood board)
const MARFIT = {
    cream: "#faf5ea",
    creamDeep: "#f2e8d1",
    terracotta: "#c67b3f",
    terracottaDark: "#a4632f",
    sage: "#7a9058",
    sageDark: "#5f7143",
    brown: "#3d2e1f",
};

const FALLBACK_PACKS = [
    { size: "5", name: "Starter", subtitle: "Experimenta 1 semana", price: 45, per_meal: 9, highlight: false, badge: "", includes: ["5 refeições completas", "Ementa fixa da semana", "Entrega numa data à escolha"] },
    { size: "10", name: "Rotina", subtitle: "5 dias · 2 refeições", price: 85, per_meal: 8.5, highlight: true, badge: "Mais escolhido", includes: ["10 refeições completas", "Ementa personalizada", "Entrega semanal"] },
    { size: "15", name: "Família", subtitle: "Todos os dias úteis", price: 120, per_meal: 8, highlight: false, badge: "", includes: ["15 refeições completas", "Ementa personalizada", "Entrega semanal"] },
    { size: "20", name: "Fitness", subtitle: "2x/dia · semana toda", price: 155, per_meal: 7.75, highlight: false, badge: "", includes: ["20 refeições completas", "Macros personalizados", "Consultoria de plano incluída"] },
];

// Real Portugal photos of Synara's meal-prep marmitas — shot in her kitchen,
// July 2026. Domain of provenance is chefsynara.com/synaraalvarenga.pt.
const GALLERY_IMAGES = {
    // azeite a ser derramado em marmita com legumes (hero shot)
    azeite: "/assets/synara/azeite.webp",
    // Synara sorrindo atrás das marmitas empilhadas
    chef: "/assets/synara/chef-marmitas.webp",
    // stack de marmitas fechadas
    stack: "/assets/synara/stack-marmitas.webp",
    // marmita salada na varanda
    varanda: "/assets/synara/salada-varanda.webp",
};

const VALUE_PROPS = [
    { Icon: Leaf, title: "Ingredientes selecionados", body: "Fornecedores locais, produto fresco, sazonal e sem processados." },
    { Icon: Scale, title: "Balanceado e nutritivo", body: "Cada marmita é pensada com macros equilibrados — proteína, hidratos e vegetais em harmonia." },
    { Icon: Heart, title: "Feito com carinho", body: "Cozinho como se fosse para mim. Sabor honesto, sem atalhos, com os temperos certos." },
];

const MarmitasLanding = () => {
    const [packs, setPacks] = useState(FALLBACK_PACKS);
    useEffect(() => {
        document.title = "MarFit Meals — Marmitas fitness · Synara Alvarenga";
        (async () => {
            try {
                const { data } = await axios.get(`${API}/marfit-packs`);
                if (Array.isArray(data) && data.length > 0) setPacks(data);
            } catch (err) {
                if (process.env.NODE_ENV !== "production") {
                    console.warn("MarFit packs fetch failed, using fallback:", err?.message || err);
                }
            }
        })();
    }, []);

    return (
        <div
            style={{ background: MARFIT.cream, color: MARFIT.brown }}
            className="min-h-screen"
            data-testid="marmitas-landing"
        >
            <Header />
            <Hero />
            <ValueProps />
            <WhoFor />
            <MidCta
                eyebrow="Vamos experimentar?"
                title="Uma semana chega para sentires a diferença."
                subtitle="Sem compromisso de longo prazo — encomendas o pack de 5 e decides depois."
                cta="Ver planos"
                href="#packs"
                variant="soft"
            />
            <HowItWorks />
            <Benefits />
            <MidCta
                eyebrow="Comer bem também é possível"
                title="Prontas para o frigorífico. Prontas para congelar 30 a 90 dias."
                subtitle="Cozinho uma vez. Comes a semana toda — ou o mês todo."
                cta="Encomendar já"
                href="#quiz"
                variant="dark"
            />
            <MealGallery />
            <ChefTestimonialMarfit />
            <TestimonialsMarfit />
            <Packs packs={packs} />
            <QuizSection />
            <Faq />
            <CrossNav />
            <Footer />
        </div>
    );
};

/* ---------- Header ---------- */
const Header = () => (
    <header
        className="sticky top-0 z-40 backdrop-blur-md border-b"
        style={{ background: `${MARFIT.cream}dd`, borderColor: `${MARFIT.brown}15` }}
    >
        <div className="max-w-6xl mx-auto px-5 py-3 flex items-center justify-between gap-3">
            <Link
                to="/"
                className="flex items-center gap-3 min-w-0 transition-opacity hover:opacity-80"
                data-testid="marmitas-logo-link"
                aria-label="Voltar ao menu principal"
            >
                <img
                    src={BRAND.logo}
                    alt="Synara Alvarenga"
                    className="h-12 w-12 sm:h-14 sm:w-14 object-contain shrink-0"
                />
                <div className="text-left leading-tight min-w-0">
                    <div
                        className="font-serif text-base sm:text-lg leading-none"
                        style={{ color: MARFIT.terracottaDark }}
                    >
                        Mar<em className="italic font-normal" style={{ color: MARFIT.sageDark }}>Fit</em>
                        <span className="ml-1 text-[9px] sm:text-[10px] tracking-[0.24em] uppercase align-middle" style={{ color: MARFIT.brown, opacity: 0.5 }}>
                            MEALS
                        </span>
                    </div>
                    <div className="text-[9px] tracking-[0.2em] uppercase mt-0.5 truncate" style={{ color: MARFIT.sage }}>
                        by Synara Alvarenga
                    </div>
                </div>
            </Link>

            <nav className="hidden lg:flex items-center gap-8 text-xs uppercase tracking-[0.18em]">
                <a href="#para-quem" className="hover:opacity-70 transition-opacity" style={{ color: MARFIT.brown }}>Para quem</a>
                <a href="#como-funciona" className="hover:opacity-70 transition-opacity" style={{ color: MARFIT.brown }}>Como funciona</a>
                <a href="#packs" className="hover:opacity-70 transition-opacity" style={{ color: MARFIT.brown }}>Planos</a>
                <a href="#faq" className="hover:opacity-70 transition-opacity" style={{ color: MARFIT.brown }}>FAQ</a>
            </nav>

            <div className="flex items-center gap-2 sm:gap-3 shrink-0">
                <Link
                    to="/personal-chef"
                    className="hidden md:inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] px-3 py-2 rounded-full ring-1 transition-colors"
                    style={{ color: MARFIT.brown, borderColor: `${MARFIT.brown}30`, background: `${MARFIT.cream}` }}
                    data-testid="marmitas-header-crossnav-chef"
                    aria-label="Ir para Personal Chef"
                >
                    <ChefHat size={13} strokeWidth={1.6} />
                    Personal Chef
                </Link>
                <a
                    href="#quiz"
                    className="text-xs sm:text-sm font-medium rounded-full px-3 sm:px-4 py-2 transition-transform hover:scale-105"
                    style={{ background: MARFIT.brown, color: MARFIT.cream }}
                    data-testid="marmitas-cta-header"
                >
                    Encomendar
                </a>
            </div>
        </div>
        {/* Mobile secondary bar: crossnav visible on small screens */}
        <div
            className="md:hidden border-t px-5 py-2 flex items-center justify-between text-[11px] uppercase tracking-[0.16em]"
            style={{ background: MARFIT.creamDeep, borderColor: `${MARFIT.brown}12`, color: MARFIT.brown }}
        >
            <Link to="/" className="inline-flex items-center gap-1 opacity-70" data-testid="marmitas-back-splash">
                <ArrowLeft size={12} /> Menu
            </Link>
            <Link
                to="/personal-chef"
                className="inline-flex items-center gap-1 font-medium"
                style={{ color: MARFIT.terracottaDark }}
                data-testid="marmitas-header-crossnav-chef-mobile"
            >
                <ChefHat size={12} strokeWidth={1.6} />
                Ir para Personal Chef
                <ArrowRight size={12} />
            </Link>
        </div>
    </header>
);

/* ---------- Hero ---------- */
const Hero = () => (
    <section className="relative overflow-hidden">
        <div className="max-w-6xl mx-auto px-5 py-14 md:py-24 grid md:grid-cols-2 gap-10 md:gap-16 items-center">
            <div>
                <motion.p
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="text-xs tracking-[0.32em] uppercase mb-4"
                    style={{ color: MARFIT.terracotta }}
                >
                    Marmitas fitness
                </motion.p>
                <motion.h1
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 0.1 }}
                    className="font-serif text-4xl sm:text-5xl md:text-6xl leading-[1.05]"
                    style={{ color: MARFIT.brown }}
                >
                    Comida <em className="italic font-normal" style={{ color: MARFIT.sageDark }}>de verdade</em>,
                    <br />feita para <em className="italic font-normal" style={{ color: MARFIT.terracottaDark }}>te fazer bem</em>.
                </motion.h1>
                <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.7, delay: 0.3 }}
                    className="mt-6 text-base md:text-lg leading-relaxed max-w-lg"
                    style={{ color: `${MARFIT.brown}cc` }}
                >
                    Marmitas frescas, equilibradas e prontas a comer.
                    Escolhe o teu pack semanal e liberta-te do preparar-limpar-repetir.
                </motion.p>
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.5 }}
                    className="mt-8 flex flex-col sm:flex-row gap-3"
                >
                    <a
                        href="#packs"
                        data-testid="hero-cta-packs"
                        className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full font-medium transition-transform hover:scale-[1.03]"
                        style={{ background: MARFIT.terracotta, color: MARFIT.cream }}
                    >
                        Ver planos e preços
                        <ArrowRight size={16} />
                    </a>
                    <a
                        href="#quiz"
                        data-testid="hero-cta-quiz"
                        className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full font-medium border-2 transition-colors"
                        style={{
                            borderColor: MARFIT.sage,
                            color: MARFIT.sageDark,
                            background: "transparent",
                        }}
                    >
                        Personalizar o meu plano
                    </a>
                </motion.div>
            </div>

            {/* Visual card with real photo */}
            <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="relative"
            >
                <div
                    className="aspect-square rounded-[2rem] relative overflow-hidden shadow-2xl"
                    style={{ background: MARFIT.creamDeep }}
                    data-testid="marfit-hero-visual"
                >
                    <img
                        src="/assets/synara/azeite.webp"
                        alt="Marmita fresca com legumes salteados a receber azeite virgem"
                        className="absolute inset-0 w-full h-full object-cover"
                    />
                    {/* Gradient overlay for text legibility */}
                    <div
                        aria-hidden
                        className="absolute inset-0"
                        style={{
                            background: `linear-gradient(to top, ${MARFIT.brown}e6 0%, ${MARFIT.brown}66 45%, transparent 75%)`,
                        }}
                    />
                    {/* Floating badge top-left */}
                    <div
                        className="absolute top-5 left-5 inline-block rounded-full px-3 py-1 text-[10px] tracking-[0.24em] uppercase font-medium backdrop-blur-sm"
                        style={{ background: `${MARFIT.cream}f0`, color: MARFIT.brown }}
                    >
                        comer bem sem pensar nisso
                    </div>
                    {/* Quote at bottom */}
                    <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8" style={{ color: MARFIT.cream }}>
                        <p className="font-serif text-lg md:text-xl leading-snug">
                            &ldquo;Preparo hoje. Comes toda a semana. Sem culpa, sem loiça, sem stress.&rdquo;
                        </p>
                        <p className="mt-3 text-xs" style={{ opacity: 0.85 }}>
                            — Synara Alvarenga
                        </p>
                    </div>
                </div>
                {/* Decorative frame */}
                <div
                    aria-hidden
                    className="absolute -bottom-3 -right-3 w-full h-full rounded-[2rem] -z-10"
                    style={{ background: MARFIT.creamDeep }}
                />
            </motion.div>
        </div>
    </section>
);

/* ---------- Value Props ---------- */
const ValueProps = () => (
    <section
        className="py-14 md:py-20"
        style={{ background: MARFIT.terracotta, color: MARFIT.cream }}
    >
        <div className="max-w-6xl mx-auto px-5">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
                {VALUE_PROPS.map(({ Icon, title, body }, i) => (
                    <motion.div
                        key={title}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, delay: i * 0.12 }}
                        className="flex flex-col"
                    >
                        <div
                            className="w-12 h-12 rounded-full flex items-center justify-center mb-4"
                            style={{ background: `${MARFIT.cream}22` }}
                        >
                            <Icon size={22} strokeWidth={1.5} />
                        </div>
                        <h3 className="font-serif text-xl mb-2">{title}</h3>
                        <p className="text-sm leading-relaxed" style={{ opacity: 0.9 }}>{body}</p>
                    </motion.div>
                ))}
            </div>
        </div>
    </section>
);

/* ---------- Packs ---------- */
const Packs = ({ packs }) => (
    <section id="packs" className="py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-5">
            <div className="text-center mb-12 md:mb-16">
                <p className="text-xs tracking-[0.32em] uppercase mb-3" style={{ color: MARFIT.terracotta }}>
                    Planos
                </p>
                <h2 className="font-serif text-3xl md:text-5xl leading-tight" style={{ color: MARFIT.brown }}>
                    Escolhe o que combina <em className="italic" style={{ color: MARFIT.sageDark }}>com a tua semana</em>
                </h2>
                <p className="mt-4 max-w-2xl mx-auto text-sm md:text-base" style={{ color: `${MARFIT.brown}b0` }}>
                    Preços indicativos por semana. Podes ajustar quantidade, entrega e preferências alimentares no formulário abaixo.
                </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
                {packs.map((p, i) => (
                    <PackCard key={p.id || p.size} pack={p} index={i} />
                ))}
            </div>
        </div>
    </section>
);

const PackCard = ({ pack, index }) => {
    const perMeal = pack.per_meal ?? pack.perMeal ?? 0;
    return (
    <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5, delay: index * 0.08 }}
        className={`relative rounded-2xl p-6 flex flex-col transition-all ${
            pack.highlight ? "shadow-xl scale-[1.02] md:scale-105" : "hover:shadow-lg"
        }`}
        style={{
            background: pack.highlight ? MARFIT.brown : MARFIT.cream,
            color: pack.highlight ? MARFIT.cream : MARFIT.brown,
            border: pack.highlight ? "none" : `1px solid ${MARFIT.brown}18`,
        }}
        data-testid={`pack-${pack.size}`}
    >
        {pack.badge && (
            <div
                className="absolute -top-3 left-1/2 -translate-x-1/2 text-[10px] uppercase tracking-[0.2em] px-3 py-1 rounded-full font-medium whitespace-nowrap"
                style={{ background: MARFIT.terracotta, color: MARFIT.cream }}
            >
                {pack.badge}
            </div>
        )}
        <div className="text-[10px] tracking-[0.28em] uppercase mb-1" style={{ color: pack.highlight ? MARFIT.terracotta : MARFIT.sage, opacity: 0.9 }}>
            {pack.name}
        </div>
        <div className="font-serif text-3xl mb-1">{pack.size} <span className="text-sm opacity-70">marmitas</span></div>
        <div className="text-xs opacity-70 mb-4">{pack.subtitle}</div>

        <div className="mb-5">
            <span className="font-serif text-4xl">€{Number(pack.price).toFixed(0)}</span>
            <span className="text-xs opacity-60 ml-2">≈ €{Number(perMeal).toFixed(2)}/marmita</span>
        </div>

        <ul className="space-y-2 mb-6 flex-1">
            {(pack.includes || []).map((item) => (
                <li key={item} className="flex items-start gap-2 text-xs">
                    <Check size={14} className="mt-0.5 shrink-0" style={{ color: pack.highlight ? MARFIT.terracotta : MARFIT.sage }} />
                    <span>{item}</span>
                </li>
            ))}
        </ul>

        <a
            href="#quiz"
            data-testid={`pack-${pack.size}-cta`}
            className="mt-auto inline-flex items-center justify-center gap-1 px-4 py-2.5 rounded-full text-sm font-medium transition-transform hover:scale-105"
            style={{
                background: pack.highlight ? MARFIT.terracotta : `${MARFIT.brown}0d`,
                color: pack.highlight ? MARFIT.cream : MARFIT.brown,
            }}
        >
            Quero este plano
            <ArrowRight size={14} />
        </a>
    </motion.div>
    );
};

/* ---------- Quiz (MarFit-specific) ---------- */
const MARFIT_STEPS = [
    {
        key: "pack",
        title: "Qual pack combina contigo?",
        subtitle: "Depois ajustamos se for preciso.",
        options: [
            { value: "5", label: "5 marmitas", desc: "Experimentar" },
            { value: "10", label: "10 marmitas", desc: "5 dias · 2 refeições" },
            { value: "15", label: "15 marmitas", desc: "Todos os dias úteis" },
            { value: "20", label: "20 marmitas", desc: "Semana completa 2x/dia" },
        ],
    },
    {
        key: "refeicoes_dia",
        title: "Que refeições queres?",
        subtitle: "Podes escolher as duas.",
        options: [
            { value: "almoço", label: "Almoço", desc: "Prato principal do meio-dia" },
            { value: "jantar", label: "Jantar", desc: "Refeição da noite" },
            { value: "ambos", label: "Almoço + Jantar", desc: "Duas refeições por dia" },
        ],
    },
    {
        key: "preferencia_alimentar",
        title: "Segues algum estilo alimentar?",
        subtitle: "Adapto os pratos ao teu perfil.",
        options: [
            { value: "sem_restricoes", label: "Sem restrições", desc: "Como um pouco de tudo" },
            { value: "vegetariano", label: "Vegetariano", desc: "Sem carne, sem peixe" },
            { value: "vegan", label: "Vegan", desc: "100% vegetal" },
            { value: "sem_gluten", label: "Sem glúten", desc: "Celíaco ou sensível" },
            { value: "low_carb", label: "Low-carb / cetogénica", desc: "Menos hidratos" },
            { value: "alta_proteina", label: "Alta proteína", desc: "Foco em ganho / performance" },
        ],
    },
    {
        key: "alergias",
        title: "Alergias ou ingredientes a evitar?",
        subtitle: "Podes escrever à vontade — se não tiveres, passa à frente.",
        type: "text",
        placeholder: "Ex.: amendoim, marisco, lactose…",
    },
    {
        key: "entrega_dia",
        title: "Melhor dia para entrega?",
        subtitle: "Entrego em toda a região do Algarve. Combinamos o dia por mensagem.",
        options: [
            { value: "segunda", label: "Segunda-feira" },
            { value: "quarta", label: "Quarta-feira" },
            { value: "sabado", label: "Sábado" },
            { value: "combinar", label: "Combinamos por mensagem" },
        ],
    },
];

const QuizSection = () => {
    const [step, setStep] = useState(0);
    const [answers, setAnswers] = useState({});
    const [contact, setContact] = useState({ nome: "", email: "", telefone: "", localidade: "", mensagem: "" });
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);
    const isContact = step === MARFIT_STEPS.length;
    const total = MARFIT_STEPS.length + 1;

    const s = MARFIT_STEPS[step];

    const pick = (val) => {
        setAnswers((a) => ({ ...a, [s.key]: val }));
        setTimeout(() => setStep((v) => Math.min(v + 1, MARFIT_STEPS.length)), 250);
    };

    const setText = (val) => setAnswers((a) => ({ ...a, [s.key]: val }));

    const canNext = s?.type === "text" || answers[s?.key];

    const validate = () => {
        if (!contact.nome.trim() || contact.nome.trim().length < 2) return "Diz-me o teu nome.";
        if (!/^\S+@\S+\.\S+$/.test(contact.email)) return "Email inválido.";
        if (!contact.telefone.trim() || contact.telefone.replace(/\D/g, "").length < 6) return "Telefone inválido.";
        if (!contact.localidade.trim()) return "Localidade obrigatória.";
        return null;
    };

    const handleSubmit = async (e) => {
        e?.preventDefault?.();
        const err = validate();
        if (err) { toast.error(err); return; }
        setSubmitting(true);
        try {
            await axios.post(`${API}/leads`, {
                ...contact,
                product: "marmitas",
                quiz: {
                    pack: answers.pack || "",
                    refeicoes_dia: answers.refeicoes_dia || "",
                    preferencia_alimentar: answers.preferencia_alimentar || "",
                    alergias: answers.alergias || "",
                    entrega_dia: answers.entrega_dia || "",
                },
            });
            setDone(true);
            toast.success("Pedido recebido! A Synara entra em contacto em breve.");
        } catch (err) {
            if (process.env.NODE_ENV !== "production") {
                console.warn("Marmitas lead failed", err?.message || err);
            }
            toast.error("Não foi possível enviar. Tenta pelo WhatsApp.");
        } finally {
            setSubmitting(false);
        }
    };

    if (done) {
        return (
            <section id="quiz" className="py-20" style={{ background: MARFIT.brown, color: MARFIT.cream }}>
                <div className="max-w-2xl mx-auto px-5 text-center">
                    <Sparkles size={40} className="mx-auto mb-4" style={{ color: MARFIT.terracotta }} />
                    <h2 className="font-serif text-3xl md:text-4xl mb-3">Obrigada, {contact.nome.split(" ")[0]}!</h2>
                    <p className="opacity-90 mb-8">
                        Recebi o teu pedido para <strong>{answers.pack || "?"} marmitas</strong> — envio-te uma proposta personalizada em breve.
                    </p>
                    <a
                        href={`https://wa.me/351924949524?text=${encodeURIComponent(`Olá! Acabei de submeter um pedido MarFit Meals — ${contact.nome}`)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-medium"
                        style={{ background: MARFIT.terracotta, color: MARFIT.cream }}
                    >
                        <MessageCircle size={16} /> Falar já no WhatsApp
                    </a>
                </div>
            </section>
        );
    }

    return (
        <section id="quiz" className="py-16 md:py-24" style={{ background: MARFIT.creamDeep }}>
            <div className="max-w-2xl mx-auto px-5">
                <div className="text-center mb-8">
                    <p className="text-xs tracking-[0.32em] uppercase mb-3" style={{ color: MARFIT.terracotta }}>
                        Personaliza
                    </p>
                    <h2 className="font-serif text-3xl md:text-4xl" style={{ color: MARFIT.brown }}>
                        Vamos montar o teu plano
                    </h2>
                </div>

                {/* progress */}
                <div className="flex gap-1.5 mb-8" data-testid="quiz-progress">
                    {Array.from({ length: total }).map((_, i) => (
                        <div
                            key={i}
                            className="h-1 flex-1 rounded-full transition-colors"
                            style={{ background: i <= step ? MARFIT.terracotta : `${MARFIT.brown}22` }}
                        />
                    ))}
                </div>

                <div className="rounded-3xl p-6 md:p-10" style={{ background: MARFIT.cream, border: `1px solid ${MARFIT.brown}12` }}>
                    <AnimatePresence mode="wait">
                        {!isContact ? (
                            <motion.div
                                key={step}
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -20 }}
                                transition={{ duration: 0.3 }}
                            >
                                <h3 className="font-serif text-2xl md:text-3xl mb-2" style={{ color: MARFIT.brown }}>
                                    {s.title}
                                </h3>
                                {s.subtitle && (
                                    <p className="text-sm mb-6" style={{ color: `${MARFIT.brown}90` }}>{s.subtitle}</p>
                                )}

                                {s.type === "text" ? (
                                    <Textarea
                                        value={answers[s.key] || ""}
                                        onChange={(e) => setText(e.target.value)}
                                        placeholder={s.placeholder}
                                        rows={3}
                                        className="mb-4"
                                        data-testid={`quiz-input-${s.key}`}
                                    />
                                ) : (
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid={`quiz-options-${s.key}`}>
                                        {s.options.map((opt) => {
                                            const selected = answers[s.key] === opt.value;
                                            return (
                                                <button
                                                    key={opt.value}
                                                    type="button"
                                                    onClick={() => pick(opt.value)}
                                                    data-testid={`quiz-opt-${s.key}-${opt.value}`}
                                                    className={`text-left rounded-xl px-4 py-3 border-2 transition-all ${
                                                        selected ? "shadow-md" : "hover:border-current"
                                                    }`}
                                                    style={{
                                                        background: selected ? MARFIT.terracotta : "transparent",
                                                        color: selected ? MARFIT.cream : MARFIT.brown,
                                                        borderColor: selected ? MARFIT.terracotta : `${MARFIT.brown}22`,
                                                    }}
                                                >
                                                    <div className="font-medium">{opt.label}</div>
                                                    {opt.desc && (
                                                        <div className="text-xs mt-0.5" style={{ opacity: 0.75 }}>
                                                            {opt.desc}
                                                        </div>
                                                    )}
                                                </button>
                                            );
                                        })}
                                    </div>
                                )}

                                <div className="mt-6 flex items-center justify-between">
                                    <button
                                        type="button"
                                        onClick={() => setStep((v) => Math.max(0, v - 1))}
                                        disabled={step === 0}
                                        data-testid="quiz-back"
                                        className="text-sm inline-flex items-center gap-1 disabled:opacity-30"
                                        style={{ color: MARFIT.brown }}
                                    >
                                        <ArrowLeft size={14} /> Anterior
                                    </button>
                                    <Button
                                        onClick={() => setStep((v) => Math.min(v + 1, MARFIT_STEPS.length))}
                                        disabled={!canNext}
                                        data-testid="quiz-next"
                                        className="rounded-full"
                                        style={{ background: MARFIT.brown, color: MARFIT.cream }}
                                    >
                                        Seguinte <ArrowRight size={14} className="ml-1" />
                                    </Button>
                                </div>
                            </motion.div>
                        ) : (
                            <motion.form
                                key="contact"
                                onSubmit={handleSubmit}
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -20 }}
                                transition={{ duration: 0.3 }}
                                data-testid="quiz-contact-form"
                            >
                                <h3 className="font-serif text-2xl md:text-3xl mb-2" style={{ color: MARFIT.brown }}>
                                    Últimos dados
                                </h3>
                                <p className="text-sm mb-6" style={{ color: `${MARFIT.brown}90` }}>
                                    Envio-te uma proposta personalizada — normalmente em 24h.
                                </p>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <Field label="Nome" required>
                                        <Input value={contact.nome} onChange={(e) => setContact((c) => ({ ...c, nome: e.target.value }))} required data-testid="contact-nome" />
                                    </Field>
                                    <Field label="Localidade" required>
                                        <Input value={contact.localidade} onChange={(e) => setContact((c) => ({ ...c, localidade: e.target.value }))} placeholder="A sua cidade na região do Algarve" required data-testid="contact-localidade" />
                                    </Field>
                                    <Field label="Email" required>
                                        <Input type="email" value={contact.email} onChange={(e) => setContact((c) => ({ ...c, email: e.target.value }))} required data-testid="contact-email" />
                                    </Field>
                                    <Field label="Telefone (WhatsApp)" required>
                                        <Input value={contact.telefone} onChange={(e) => setContact((c) => ({ ...c, telefone: e.target.value }))} required data-testid="contact-telefone" />
                                    </Field>
                                </div>
                                <Field label="Nota (opcional)" className="mt-4">
                                    <Textarea rows={2} value={contact.mensagem} onChange={(e) => setContact((c) => ({ ...c, mensagem: e.target.value }))} placeholder="Algum pedido especial?" data-testid="contact-mensagem" />
                                </Field>

                                <div className="mt-6 flex items-center justify-between">
                                    <button
                                        type="button"
                                        onClick={() => setStep((v) => v - 1)}
                                        className="text-sm inline-flex items-center gap-1"
                                        data-testid="contact-back"
                                        style={{ color: MARFIT.brown }}
                                    >
                                        <ArrowLeft size={14} /> Anterior
                                    </button>
                                    <Button
                                        type="submit"
                                        disabled={submitting}
                                        data-testid="contact-submit"
                                        className="rounded-full"
                                        style={{ background: MARFIT.terracotta, color: MARFIT.cream }}
                                    >
                                        {submitting ? <Loader2 className="animate-spin mr-2" size={14} /> : null}
                                        Pedir proposta
                                    </Button>
                                </div>
                            </motion.form>
                        )}
                    </AnimatePresence>
                </div>
            </div>
        </section>
    );
};

const Field = ({ label, required, className = "", children }) => (
    <div className={className}>
        <Label className="text-xs uppercase tracking-[0.16em] mb-1.5 block" style={{ color: `${MARFIT.brown}90` }}>
            {label}{required && <span style={{ color: MARFIT.terracotta }}> *</span>}
        </Label>
        {children}
    </div>
);

/* ---------- FAQ ---------- */
const FAQS = [
    {
        q: "Como funciona a entrega?",
        a: "Entrego pessoalmente em toda a região do Algarve no dia combinado — Faro, Loulé, Albufeira, Portimão, Lagos, Tavira, Vilamoura e restantes cidades. Combinamos o dia e a morada por mensagem. Marmitas vêm frescas em caixas isotérmicas e podem ir direto ao frigorífico.",
    },
    {
        q: "Quantos dias duram as marmitas?",
        a: "No frigorífico duram entre 4 a 5 dias. Se quiseres esticar mais, congela — na secção seguinte explico como.",
    },
    {
        q: "Posso congelar as marmitas? Por quanto tempo?",
        a: "Sim, todas as marmitas podem ir ao congelador. Duram entre 30 a 90 dias congeladas (dependendo do prato — carnes e estufados aguentam melhor os 90 dias; pratos com molhos leves ou vegetais mais delicados ficam melhores até 30-45 dias). Descongela sempre no frigorífico durante a noite e aquece só a porção que vais comer.",
    },
    {
        q: "Posso mudar o meu plano de semana para semana?",
        a: "Sim! Ajustas quantidade, preferências e alergias sempre que quiseres. Basta avisar até 48h antes da entrega.",
    },
    {
        q: "Como pago?",
        a: "MB Way ou transferência bancária. Envio o link de pagamento junto com a proposta.",
    },
    {
        q: "As marmitas são fitness / low-calorie?",
        a: "Todas são equilibradas, mas ajusto os macros ao teu objetivo — perder peso, ganhar massa, manter energia. Diz-me no formulário.",
    },
];

const Faq = () => {
    const [open, setOpen] = useState(null);
    return (
        <section id="faq" className="py-16 md:py-24" style={{ background: MARFIT.cream }}>
            <div className="max-w-2xl mx-auto px-5">
                <div className="text-center mb-10">
                    <p className="text-xs tracking-[0.32em] uppercase mb-3" style={{ color: MARFIT.sage }}>
                        Perguntas frequentes
                    </p>
                    <h2 className="font-serif text-3xl md:text-4xl" style={{ color: MARFIT.brown }}>
                        Ainda tens dúvidas?
                    </h2>
                </div>
                <div className="space-y-3">
                    {FAQS.map((f, i) => (
                        <div
                            key={f.q}
                            className="rounded-xl overflow-hidden"
                            style={{ border: `1px solid ${MARFIT.brown}18`, background: MARFIT.creamDeep }}
                            data-testid={`faq-${i}`}
                        >
                            <button
                                type="button"
                                onClick={() => setOpen(open === i ? null : i)}
                                className="w-full flex items-center justify-between text-left px-4 py-4"
                                style={{ color: MARFIT.brown }}
                            >
                                <span className="font-medium text-sm md:text-base">{f.q}</span>
                                <ChevronDown
                                    size={18}
                                    className="transition-transform shrink-0"
                                    style={{
                                        transform: open === i ? "rotate(180deg)" : "rotate(0)",
                                        color: MARFIT.terracotta,
                                    }}
                                />
                            </button>
                            {open === i && (
                                <div
                                    className="px-4 pb-4 text-sm leading-relaxed"
                                    style={{ color: `${MARFIT.brown}c0` }}
                                >
                                    {f.a}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
};

/* ---------- Footer ---------- */
const Footer = () => (
    <footer
        className="py-10 text-center text-xs"
        style={{ background: MARFIT.brown, color: `${MARFIT.cream}b0` }}
    >
        <div className="max-w-3xl mx-auto px-5">
            <div className="font-serif text-lg mb-2" style={{ color: MARFIT.cream }}>
                Mar<em className="italic font-normal" style={{ color: MARFIT.terracotta }}>Fit</em> Meals
            </div>
            <div className="text-[10px] tracking-[0.28em] uppercase mb-6" style={{ color: MARFIT.sage }}>
                Nutrição que te move ♡
            </div>
            <div className="flex justify-center gap-6 mb-4">
                <a
                    href="https://wa.me/351924949524"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:opacity-100 transition-opacity"
                    style={{ opacity: 0.7 }}
                >
                    WhatsApp
                </a>
                <a
                    href="mailto:synarapersonalchef@gmail.com"
                    className="hover:opacity-100 transition-opacity"
                    style={{ opacity: 0.7 }}
                >
                    synarapersonalchef@gmail.com
                </a>
                <Link
                    to="/personal-chef"
                    className="hover:opacity-100 transition-opacity"
                    style={{ opacity: 0.7 }}
                >
                    Personal Chef
                </Link>
            </div>
            <div className="opacity-50">© {new Date().getFullYear()} Synara Alvarenga · Todos os direitos reservados.</div>
        </div>
    </footer>
);

export default MarmitasLanding;

/* ---------- Meal Gallery ---------- */
const GALLERY = [
    { key: "azeite", label: "Azeite virgem, tempero honesto", span: "col-span-2 row-span-2", pos: "center" },
    { key: "chef", label: "Feitas à mão pela Synara", span: "col-span-1 row-span-2", pos: "center 35%" },
    { key: "stack", label: "Prontas para a semana", span: "col-span-1 row-span-1", pos: "center" },
    { key: "varanda", label: "Fresco todos os dias", span: "col-span-1 row-span-1", pos: "center" },
];

function MealGallery() {
    return (
        <section className="py-16 md:py-24" style={{ background: MARFIT.creamDeep }}>
            <div className="max-w-6xl mx-auto px-5">
                <div className="text-center mb-10 md:mb-12">
                    <p className="text-xs tracking-[0.32em] uppercase mb-3" style={{ color: MARFIT.sage }}>
                        Feitas em Portugal
                    </p>
                    <h2 className="font-serif text-3xl md:text-5xl leading-tight" style={{ color: MARFIT.brown }}>
                        Comida <em className="italic" style={{ color: MARFIT.terracottaDark }}>real</em>, feita à mão
                    </h2>
                    <p className="mt-3 text-sm md:text-base max-w-xl mx-auto" style={{ color: `${MARFIT.brown}a0` }}>
                        Nada de fotos genéricas de banco de imagens. Estas são marmitas
                        preparadas pela Synara, na cozinha dela, no Algarve.
                    </p>
                </div>
                <div
                    className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 auto-rows-[140px] md:auto-rows-[190px]"
                    data-testid="meal-gallery"
                >
                    {GALLERY.map((g, i) => (
                        <motion.figure
                            key={g.key}
                            initial={{ opacity: 0, scale: 0.96 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.5, delay: i * 0.08 }}
                            className={`relative overflow-hidden rounded-2xl group ${g.span}`}
                            style={{ background: MARFIT.cream }}
                        >
                            <img
                                src={GALLERY_IMAGES[g.key]}
                                alt={g.label}
                                loading="lazy"
                                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                                style={{ objectPosition: g.pos || "center" }}
                            />
                            <figcaption
                                className="absolute bottom-0 left-0 right-0 p-3 text-[10px] uppercase tracking-[0.24em] font-medium"
                                style={{
                                    background: "linear-gradient(to top, rgba(0,0,0,0.55), transparent)",
                                    color: MARFIT.cream,
                                }}
                            >
                                {g.label}
                            </figcaption>
                        </motion.figure>
                    ))}
                </div>
            </div>
        </section>
    );
}

/* ---------- Cross-navigation to Personal Chef ---------- */
function CrossNav() {
    return (
        <section
            className="py-14 md:py-20"
            style={{ background: `linear-gradient(135deg, ${MARFIT.brown} 0%, ${MARFIT.sageDark} 100%)` }}
        >
            <div className="max-w-4xl mx-auto px-5 text-center">
                <ChefHat
                    size={32}
                    strokeWidth={1.4}
                    className="mx-auto mb-4"
                    style={{ color: MARFIT.terracotta }}
                />
                <p className="text-[11px] tracking-[0.32em] uppercase mb-3" style={{ color: MARFIT.cream, opacity: 0.7 }}>
                    Também jantares e eventos
                </p>
                <h3
                    className="font-serif text-2xl md:text-4xl mb-4 leading-tight"
                    style={{ color: MARFIT.cream }}
                >
                    Precisas de um <em className="italic" style={{ color: MARFIT.terracotta }}>Personal Chef</em> para uma ocasião especial?
                </h3>
                <p className="max-w-xl mx-auto mb-6 text-sm md:text-base" style={{ color: MARFIT.cream, opacity: 0.85 }}>
                    Jantares privados, aniversários, brunches ou eventos corporativos —
                    a Synara também cozinha na sua casa.
                </p>
                <Link
                    to="/personal-chef"
                    data-testid="crossnav-to-chef"
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-medium transition-transform hover:scale-105"
                    style={{ background: MARFIT.cream, color: MARFIT.brown }}
                >
                    Conhecer o serviço Personal Chef
                    <ArrowRight size={16} />
                </Link>
            </div>
        </section>
    );
}


/* ---------- Para quem é (WhoFor) ---------- */
const WHOFOR_ITEMS = [
    {
        key: "profissionais",
        Icon: Briefcase,
        title: "Profissionais ocupados",
        desc: "Chegas a casa sem energia para cozinhar? Almoços e jantares prontos em minutos.",
        image: "/assets/synara/azeite.webp",
        size: "tall",
    },
    {
        key: "atletas",
        Icon: Dumbbell,
        title: "Atletas e gym-goers",
        desc: "Macros ajustados ao teu objetivo — perda de peso, ganho ou performance.",
        image: "/assets/synara/whofor-atletas.jpg",
        pos: "center",
    },
    {
        key: "casais",
        Icon: Heart,
        title: "Casais fit",
        desc: "Comer bem a dois sem passar horas na cozinha.",
        image: "/assets/synara/whofor-casais.jpg",
        pos: "center",
    },
    {
        key: "familias",
        Icon: Baby,
        title: "Famílias sem tempo",
        desc: "Refeições completas, saudáveis, prontas para toda a família.",
        image: "/assets/synara/chef-marmitas.webp",
        size: "wide",
        pos: "center 30%",
    },
    {
        key: "escritorios",
        Icon: Users,
        title: "Escritórios e co-workings",
        desc: "Packs semanais para levar para o trabalho. Aquece e come, sem takeaways.",
        image: "/assets/synara/whofor-escritorios.jpg",
        pos: "center",
    },
    {
        key: "mealprep",
        Icon: CalendarRange,
        title: "Meal-prep semanal",
        desc: "Prepara a semana toda de uma vez. Ganha tempo, poupa dinheiro, come melhor.",
        image: "/assets/synara/whofor-mealprep.jpg",
        pos: "center",
    },
];

function WhoFor() {
    return (
        <section
            id="para-quem"
            className="py-20 md:py-28"
            style={{ background: MARFIT.cream }}
            data-testid="marfit-whofor-section"
        >
            <div className="max-w-6xl mx-auto px-5">
                <div className="max-w-2xl mb-12 md:mb-16">
                    <p className="text-xs tracking-[0.32em] uppercase mb-3" style={{ color: MARFIT.terracotta }}>
                        Para quem é
                    </p>
                    <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl leading-[1.05]" style={{ color: MARFIT.brown }}>
                        Feito para quem quer <em className="italic font-normal" style={{ color: MARFIT.sageDark }}>comer melhor</em> sem perder tempo.
                    </h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5 auto-rows-[1fr]">
                    {WHOFOR_ITEMS.map((item, i) => {
                        const Icon = item.Icon;
                        const hasImage = !!item.image;
                        return (
                            <motion.div
                                key={item.key}
                                initial={{ opacity: 0, y: 24 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-60px" }}
                                transition={{ duration: 0.6, delay: i * 0.06 }}
                                className={`relative overflow-hidden rounded-2xl group ${
                                    item.size === "tall" ? "md:row-span-2" : ""
                                } ${item.size === "wide" ? "md:col-span-2" : ""}`}
                                style={{
                                    minHeight: item.size === "tall" ? 480 : 260,
                                    background: hasImage ? MARFIT.brown : MARFIT.creamDeep,
                                    border: `1px solid ${MARFIT.brown}18`,
                                }}
                                data-testid={`whofor-card-${item.key}`}
                            >
                                {hasImage ? (
                                    <>
                                        <img
                                            src={item.image}
                                            alt=""
                                            className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                                            style={{ objectPosition: item.pos || "center" }}
                                        />
                                        <div className="absolute inset-0" style={{ background: `linear-gradient(to top, ${MARFIT.brown}dd 0%, ${MARFIT.brown}30 55%, transparent 100%)` }} />
                                        <div className="relative h-full p-6 md:p-7 flex flex-col justify-end" style={{ color: MARFIT.cream }}>
                                            <Icon size={22} strokeWidth={1.5} style={{ color: MARFIT.terracotta }} className="mb-3" />
                                            <h3 className="font-serif text-2xl md:text-3xl leading-tight">{item.title}</h3>
                                            <p className="mt-2 text-sm md:text-base opacity-90">{item.desc}</p>
                                        </div>
                                    </>
                                ) : (
                                    <div className="relative h-full p-6 md:p-7 flex flex-col justify-between">
                                        <Icon size={26} strokeWidth={1.4} style={{ color: MARFIT.terracottaDark }} />
                                        <div>
                                            <h3 className="font-serif text-2xl md:text-3xl leading-tight" style={{ color: MARFIT.brown }}>
                                                {item.title}
                                            </h3>
                                            <p className="mt-2 text-sm md:text-base" style={{ color: `${MARFIT.brown}b0` }}>
                                                {item.desc}
                                            </p>
                                        </div>
                                    </div>
                                )}
                            </motion.div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}

/* ---------- Como funciona (HowItWorks) ---------- */
const HOW_STEPS = [
    {
        title: "Escolhes o teu pack",
        desc: "5, 10, 15 ou 20 marmitas por semana. Diz-me também o teu objetivo (perder peso, ganhar massa, manter energia).",
    },
    {
        title: "Personalizamos juntos",
        desc: "Ajusto macros, preferências alimentares, alergias e sabores. Cardápio rotativo — nunca comes duas semanas iguais.",
    },
    {
        title: "Cozinho fresco",
        desc: "Preparo tudo no início da semana com ingredientes frescos e locais, sem processados nem conservantes.",
    },
    {
        title: "Entrego à tua porta",
        desc: "Entrego em toda a região do Algarve — Faro, Loulé, Albufeira, Portimão, Lagos, Tavira, Vilamoura e demais cidades. Marmitas em embalagem prática, prontas a comer — no frigorífico até 5 dias, no congelador 30-90 dias.",
    },
];

function HowItWorks() {
    return (
        <section
            id="como-funciona"
            className="py-20 md:py-28"
            style={{ background: MARFIT.creamDeep }}
            data-testid="marfit-how-section"
        >
            <div className="max-w-5xl mx-auto px-5">
                <div className="text-center max-w-2xl mx-auto mb-14 md:mb-20">
                    <p className="text-xs tracking-[0.32em] uppercase mb-3" style={{ color: MARFIT.sage }}>
                        Como funciona
                    </p>
                    <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl leading-[1.05]" style={{ color: MARFIT.brown }}>
                        Simples <em className="italic font-normal" style={{ color: MARFIT.terracottaDark }}>como comer</em>.
                    </h2>
                </div>
                <div className="grid md:grid-cols-2 gap-x-14 gap-y-12">
                    {HOW_STEPS.map((s, i) => (
                        <motion.div
                            key={s.title}
                            initial={{ opacity: 0, y: 24 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-60px" }}
                            transition={{ duration: 0.6, delay: i * 0.08 }}
                            className="flex gap-5 md:gap-7 pb-8"
                            style={{ borderBottom: `1px solid ${MARFIT.brown}18` }}
                            data-testid={`marfit-step-${i + 1}`}
                        >
                            <div
                                className="font-serif italic text-5xl md:text-6xl leading-none shrink-0"
                                style={{ color: MARFIT.terracotta }}
                            >
                                {String(i + 1).padStart(2, "0")}
                            </div>
                            <div>
                                <h3 className="font-serif text-2xl md:text-3xl leading-tight" style={{ color: MARFIT.brown }}>
                                    {s.title}
                                </h3>
                                <p className="mt-3 leading-relaxed" style={{ color: `${MARFIT.brown}b8` }}>
                                    {s.desc}
                                </p>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
}

/* ---------- Benefícios (Benefits) ---------- */
const BENEFIT_ITEMS = [
    { Icon: Clock, title: "Poupas 6-10h por semana" },
    { Icon: Sprout, title: "Ingredientes frescos e locais" },
    { Icon: HeartPulse, title: "Macros ajustados ao teu objetivo" },
    { Icon: Snowflake, title: "Congela até 90 dias" },
    { Icon: SaladIcon, title: "Sem processados nem conservantes" },
    { Icon: Repeat, title: "Cardápio rotativo semanal" },
    { Icon: Leaf, title: "Embalagem prática, pronta a comer" },
    { Icon: Truck, title: "Entrega em toda a região do Algarve" },
];

function Benefits() {
    return (
        <section
            id="beneficios"
            className="py-20 md:py-28"
            style={{ background: MARFIT.cream }}
            data-testid="marfit-benefits-section"
        >
            <div className="max-w-6xl mx-auto px-5">
                <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 mb-12 md:mb-16">
                    <div className="lg:col-span-5">
                        <p className="text-xs tracking-[0.32em] uppercase mb-3" style={{ color: MARFIT.terracotta }}>
                            Benefícios
                        </p>
                        <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl leading-[1.05]" style={{ color: MARFIT.brown }}>
                            Comer bem <em className="italic font-normal" style={{ color: MARFIT.sageDark }}>não é sacrifício</em>. É hábito.
                        </h2>
                    </div>
                    <div className="lg:col-span-6 lg:col-start-7 flex items-end">
                        <p className="text-base md:text-lg leading-relaxed" style={{ color: `${MARFIT.brown}b0` }}>
                            Tudo o que ganhas ao ter as tuas refeições prontas — mais tempo, menos culpa, mais energia, menos loiça.
                        </p>
                    </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-5">
                    {BENEFIT_ITEMS.map(({ Icon, title }, i) => (
                        <motion.div
                            key={title}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-60px" }}
                            transition={{ duration: 0.55, delay: i * 0.05 }}
                            className="p-6 md:p-7 rounded-2xl transition-all duration-500 hover:-translate-y-1"
                            style={{ background: MARFIT.creamDeep, border: `1px solid ${MARFIT.brown}18` }}
                            data-testid={`marfit-benefit-${i}`}
                        >
                            <Icon size={26} strokeWidth={1.3} className="mb-5" style={{ color: MARFIT.terracottaDark }} />
                            <h3 className="font-serif text-lg md:text-xl leading-tight" style={{ color: MARFIT.brown }}>
                                {title}
                            </h3>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
}

/* ---------- A Chef (ChefTestimonialMarfit) ---------- */
function ChefTestimonialMarfit() {
    return (
        <section
            id="sobre-a-chef"
            className="py-20 md:py-28 relative overflow-hidden"
            style={{ background: MARFIT.brown, color: MARFIT.cream }}
            data-testid="marfit-chef-section"
        >
            <div className="max-w-6xl mx-auto px-5 grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
                <motion.div
                    initial={{ opacity: 0, x: -30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8 }}
                    className="lg:col-span-6"
                >
                    <div className="relative aspect-[4/5] rounded-3xl overflow-hidden">
                        <img
                            src={BRAND.chefPhoto}
                            alt="Synara Alvarenga"
                            className="absolute inset-0 w-full h-full object-cover"
                        />
                        <div className="absolute inset-0" style={{ background: `linear-gradient(to top, ${MARFIT.brown}55, transparent)` }} />
                        <div
                            className="absolute left-5 bottom-5 right-5 sm:right-auto sm:max-w-[260px] backdrop-blur rounded-2xl px-5 py-4 shadow-xl"
                            style={{ background: `${MARFIT.cream}f2` }}
                        >
                            <div className="flex items-center gap-2 mb-1">
                                <span className="inline-block w-10 h-px" style={{ background: MARFIT.terracottaDark }} />
                                <span className="text-[10px] uppercase tracking-[0.22em]" style={{ color: `${MARFIT.brown}b0` }}>
                                    Cozinha viva desde 2018
                                </span>
                            </div>
                            <p className="font-serif text-lg leading-tight" style={{ color: MARFIT.brown }}>
                                Comida real, sem atalhos.
                            </p>
                        </div>
                    </div>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, x: 30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8 }}
                    className="lg:col-span-6"
                >
                    <div className="flex items-center gap-3 mb-5">
                        <span className="inline-block w-12 h-px" style={{ background: MARFIT.terracotta }} />
                        <span className="text-xs uppercase tracking-[0.24em]" style={{ color: `${MARFIT.cream}90` }}>
                            A chef por trás
                        </span>
                    </div>
                    <Quote size={40} strokeWidth={1} className="mb-5" style={{ color: MARFIT.terracotta }} />
                    <blockquote
                        className="font-serif text-2xl md:text-3xl lg:text-4xl leading-[1.2]"
                    >
                        Preparo cada marmita como se fosse para mim. Comida real, sem atalhos, com sabor honesto —
                        <em className="italic font-normal" style={{ color: MARFIT.terracotta }}>
                            {" "}porque comer bem não é sacrifício, é hábito.
                        </em>
                    </blockquote>
                    <div className="mt-8 flex items-center gap-4">
                        <img src={BRAND.logo} alt="Synara Alvarenga" className="h-16 w-16 object-contain" />
                        <div>
                            <div className="font-serif text-xl" style={{ color: MARFIT.cream }}>Synara Alvarenga</div>
                            <div className="text-xs uppercase tracking-[0.22em] mt-1" style={{ color: `${MARFIT.cream}80` }}>
                                Personal Chef & Meal Prep
                            </div>
                        </div>
                    </div>
                </motion.div>
            </div>
        </section>
    );
}

/* ---------- Testimonials (reais — screenshots de conversas Instagram) ---------- */
const MARFIT_TESTIMONIALS = [
    {
        name: "John",
        role: "Cliente MarFit",
        image: "/assets/synara/testim-c.webp",
    },
    {
        name: "Cliente Vilamoura",
        role: "Vilamoura · Algarve",
        image: "/assets/synara/testim-b.webp",
    },
    {
        name: "Cliente Personal Chef",
        role: "Semanal · em curso",
        image: "/assets/synara/testim-a.webp",
    },
    {
        name: "Mari",
        role: "Cliente MarFit",
        image: "/assets/synara/testim-d.webp",
    },
];

function TestimonialsMarfit() {
    return (
        <section
            id="testemunhos"
            className="py-20 md:py-28"
            style={{ background: MARFIT.creamDeep }}
            data-testid="marfit-testimonials-section"
        >
            <div className="max-w-6xl mx-auto px-5">
                <div className="text-center max-w-2xl mx-auto mb-12 md:mb-16">
                    <p className="text-xs tracking-[0.32em] uppercase mb-3" style={{ color: MARFIT.sage }}>
                        Quem já experimentou
                    </p>
                    <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl leading-[1.05]" style={{ color: MARFIT.brown }}>
                        Testemunhos <em className="italic font-normal" style={{ color: MARFIT.terracottaDark }}>reais</em> dos clientes.
                    </h2>
                    <p className="mt-4 text-sm md:text-base" style={{ color: `${MARFIT.brown}b0` }}>
                        Conversas verdadeiras — capturadas do WhatsApp / Instagram, partilhadas com autorização.
                    </p>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-5">
                    {MARFIT_TESTIMONIALS.map((t, i) => (
                        <motion.figure
                            key={i}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-60px" }}
                            transition={{ duration: 0.6, delay: i * 0.07 }}
                            className="flex flex-col rounded-2xl overflow-hidden group"
                            style={{ background: MARFIT.cream, border: `1px solid ${MARFIT.brown}18` }}
                            data-testid={`marfit-testimonial-${i}`}
                        >
                            <div className="relative overflow-hidden" style={{ aspectRatio: "9/16" }}>
                                <img
                                    src={t.image}
                                    alt={`Testemunho de ${t.name}`}
                                    loading="lazy"
                                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                                />
                            </div>
                            <figcaption
                                className="px-4 py-4 flex items-center justify-between gap-2"
                                style={{ borderTop: `1px solid ${MARFIT.brown}12` }}
                            >
                                <div className="min-w-0">
                                    <div className="font-medium text-sm truncate" style={{ color: MARFIT.brown }}>
                                        {t.name}
                                    </div>
                                    <div className="text-[10px] uppercase tracking-[0.14em] mt-0.5 truncate" style={{ color: `${MARFIT.brown}80` }}>
                                        {t.role}
                                    </div>
                                </div>
                                <div className="flex items-center gap-0.5 shrink-0" style={{ color: MARFIT.terracotta }}>
                                    {Array.from({ length: 5 }).map((_, k) => (
                                        <Star key={k} size={10} fill="currentColor" strokeWidth={0} />
                                    ))}
                                </div>
                            </figcaption>
                        </motion.figure>
                    ))}
                </div>
            </div>
        </section>
    );
}

/* ---------- Mid-page CTA (SectionCta equivalent) ---------- */
function MidCta({ eyebrow, title, subtitle, cta, href = "#quiz", variant = "soft" }) {
    if (variant === "dark") {
        return (
            <section
                className="relative py-16 md:py-24 overflow-hidden"
                style={{ background: `linear-gradient(135deg, ${MARFIT.brown} 0%, ${MARFIT.terracottaDark} 100%)`, color: MARFIT.cream }}
                data-testid="marfit-midcta-dark"
            >
                <div className="max-w-3xl mx-auto px-5 text-center relative">
                    {eyebrow && (
                        <div className="flex items-center justify-center gap-3 mb-5">
                            <span className="inline-block w-10 h-px" style={{ background: MARFIT.terracotta }} />
                            <span className="text-xs uppercase tracking-[0.24em]" style={{ color: `${MARFIT.cream}b0` }}>
                                {eyebrow}
                            </span>
                            <span className="inline-block w-10 h-px" style={{ background: MARFIT.terracotta }} />
                        </div>
                    )}
                    <h3 className="font-serif text-3xl md:text-4xl lg:text-5xl leading-[1.1]">
                        {title}
                    </h3>
                    {subtitle && (
                        <p className="mt-4 max-w-xl mx-auto text-base md:text-lg" style={{ color: `${MARFIT.cream}cc` }}>
                            {subtitle}
                        </p>
                    )}
                    <a
                        href={href}
                        className="inline-flex items-center gap-2 mt-8 px-7 py-3.5 rounded-full font-medium text-base transition-transform hover:scale-105"
                        style={{ background: MARFIT.cream, color: MARFIT.brown }}
                        data-testid="marfit-midcta-dark-button"
                    >
                        {cta}
                        <ArrowRight size={16} />
                    </a>
                </div>
            </section>
        );
    }
    // soft
    return (
        <section className="py-14 md:py-20" data-testid="marfit-midcta-soft">
            <div className="max-w-5xl mx-auto px-5">
                <div
                    className="rounded-3xl px-7 py-10 md:px-14 md:py-14 relative overflow-hidden grid md:grid-cols-12 gap-6 items-center"
                    style={{ background: MARFIT.creamDeep, border: `1px solid ${MARFIT.brown}18` }}
                >
                    <Sparkles size={20} className="absolute top-6 right-6 opacity-60" style={{ color: MARFIT.terracotta }} />
                    <div className="md:col-span-8">
                        {eyebrow && (
                            <p className="text-xs tracking-[0.24em] uppercase mb-3" style={{ color: MARFIT.sage }}>
                                {eyebrow}
                            </p>
                        )}
                        <h3 className="font-serif text-2xl sm:text-3xl md:text-4xl leading-tight" style={{ color: MARFIT.brown }}>
                            {title}
                        </h3>
                        {subtitle && (
                            <p className="mt-3 leading-relaxed" style={{ color: `${MARFIT.brown}b0` }}>
                                {subtitle}
                            </p>
                        )}
                    </div>
                    <div className="md:col-span-4 md:text-right">
                        <a
                            href={href}
                            className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-medium transition-transform hover:scale-105"
                            style={{ background: MARFIT.brown, color: MARFIT.cream }}
                            data-testid="marfit-midcta-soft-button"
                        >
                            {cta}
                            <ArrowRight size={16} />
                        </a>
                    </div>
                </div>
            </div>
        </section>
    );
}
